import React, { useState, useEffect } from 'react';

interface TipInputProps {
  baseAmount: number; // The amount to calculate tip percentage on
  tipAmount: number;
  onTipChange: (tip: number) => void;
  showCustomInput?: boolean;
  className?: string;
}

const TipInput: React.FC<TipInputProps> = ({
  baseAmount,
  tipAmount,
  onTipChange,
  showCustomInput = true,
  className = '',
}) => {
  const [customTipInput, setCustomTipInput] = useState('');
  const [selectedPercentage, setSelectedPercentage] = useState<number | null>(null);

  const tipPercentages = [15, 18, 20, 25];

  useEffect(() => {
    // Update custom input when tip changes externally
    if (tipAmount > 0 && selectedPercentage === null) {
      setCustomTipInput(tipAmount.toFixed(2));
    }
  }, [tipAmount, selectedPercentage]);

  const handlePercentageClick = (percentage: number) => {
    const calculatedTip = Math.round((baseAmount * percentage / 100) * 100) / 100;
    setSelectedPercentage(percentage);
    setCustomTipInput(calculatedTip.toFixed(2));
    onTipChange(calculatedTip);
  };

  const handleCustomInputChange = (value: string) => {
    setCustomTipInput(value);
    setSelectedPercentage(null);
    
    // Parse the input value
    const normalized = value.replace(',', '.');
    const parsed = parseFloat(normalized);
    
    if (!isNaN(parsed) && parsed >= 0) {
      const rounded = Math.round(parsed * 100) / 100;
      onTipChange(rounded);
    } else if (value === '' || value === '.') {
      onTipChange(0);
    }
  };

  const handleClearTip = () => {
    setCustomTipInput('');
    setSelectedPercentage(null);
    onTipChange(0);
  };

  const totalAmount = baseAmount + tipAmount;
  const tipPercentage = baseAmount > 0 ? Math.round((tipAmount / baseAmount) * 100 * 100) / 100 : 0;

  return (
    <div className={`${className}`}>
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Add Tip (Optional)
        </label>
        <div className="grid grid-cols-4 gap-2 mb-3">
          {tipPercentages.map((percentage) => {
            const calculatedTip = Math.round((baseAmount * percentage / 100) * 100) / 100;
            const isSelected = selectedPercentage === percentage || 
              (tipAmount > 0 && Math.abs(tipAmount - calculatedTip) < 0.01);
            
            return (
              <button
                key={percentage}
                type="button"
                onClick={() => handlePercentageClick(percentage)}
                className={`py-2 px-3 rounded-lg font-semibold text-sm transition-colors ${
                  isSelected
                    ? 'bg-pink-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {percentage}%
              </button>
            );
          })}
        </div>
        
        {showCustomInput && (
          <div className="flex gap-2">
            <div className="flex-1">
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="text"
                  value={customTipInput}
                  onChange={(e) => handleCustomInputChange(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 bg-gray-100 text-gray-900"
                />
              </div>
            </div>
            {tipAmount > 0 && (
              <button
                type="button"
                onClick={handleClearTip}
                className="px-3 py-2 text-gray-600 hover:text-gray-800 text-sm"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {tipAmount > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm text-gray-600">Subtotal:</span>
            <span className="text-sm font-semibold text-gray-900">${baseAmount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm text-gray-600">Tip ({tipPercentage}%):</span>
            <span className="text-sm font-semibold text-pink-600">${tipAmount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-gray-300">
            <span className="text-base font-bold text-gray-900">Total:</span>
            <span className="text-lg font-bold text-gray-900">${totalAmount.toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TipInput;

